import { useMemo } from 'react';
import { Stack, SxProps, Theme, Typography } from '@mui/material';
import { TransitionGroup } from 'react-transition-group';

import PanelNav from './PanelNav';
import RemainingCard from './RemainingCard';
import useCarouselPanels from './useCarouselPanels';
import useTaskCarouselCelebration from './useTaskCarouselCelebration';

import TaskCard from 'modules/Tasks/TaskCard';
import TaskCarouselCelebration from 'modules/Tasks/TaskCarouselCelebration';
import { MemberTaskFeedItemTypes, MemberTaskFeedItem, TaskLocation } from 'modules/Tasks/types';
import TaskGroupCard from 'modules/Tasks/Groups/TaskGroupCard';
import FeedItemTransition from 'modules/Tasks/common/FeedItemTransition';
import useSettledEmpty from 'modules/Tasks/hooks/useSettledEmpty';
import { memberTaskFeedItemKey, isFrameworkMemberTaskFeedItem } from 'modules/Tasks/utils/memberTaskFeedItem';
import useMemberPriorityTasks from 'services/memberTasks/queries/useMemberPriorityTasks';
import useMemberTaskFeed from 'services/memberTasks/queries/useMemberTaskFeed';
import LOCATION_TO_AREA from 'modules/Tasks/utils/locationToArea';

function CarouselItem({
    item,
    page,
    isCompleting,
}: Readonly<{ item: MemberTaskFeedItem; page?: string; isCompleting?: boolean }>) {
    if (item.item_type === MemberTaskFeedItemTypes.GROUP) {
        return <TaskGroupCard group={item.group} isCompleting={isCompleting} />;
    }
    return <TaskCard task={item.task} page={page} isCompleting={isCompleting} />;
}

const PRIORITY_FEED_LIMIT = 5;

type TaskCarouselProps = {
    enableCelebration?: boolean;
    sx?: SxProps<Theme>;
    /** Heading above the cards. */
    title?: string;
    /**
     * `ranked` takes the server's slice as-is: highest ranked first, capped server-side.
     * `dueDate` reads the same feed the Tasks page does — earliest due date first, undated last —
     * and caps client-side, so the cards are the soonest due rather than the top-ranked reordered.
     */
    order?: 'ranked' | 'dueDate';
} & ({ surface: 'dashboard'; location?: never } | { surface: 'location'; location: TaskLocation });

export default function TaskCarousel({
    surface,
    location,
    enableCelebration = false,
    sx,
    title = 'Priority tasks',
    order = 'ranked',
}: Readonly<TaskCarouselProps>) {
    // Both hooks run every render, as hooks must; `enabled` keeps the unused one from fetching.
    const isDueDateOrder = order === 'dueDate';
    const priorityQuery = useMemberPriorityTasks(
        surface === 'location' && location
            ? { surface: 'location', location, limit: PRIORITY_FEED_LIMIT }
            : { surface: 'dashboard', limit: PRIORITY_FEED_LIMIT },
        { enabled: !isDueDateOrder }
    );
    const dueDateFeed = useMemberTaskFeed(location, { enabled: isDueDateOrder });
    const isError = isDueDateOrder ? dueDateFeed.isError : priorityQuery.isError;

    const { visibleItems, remainingCount } = useMemo(() => {
        if (isDueDateOrder) {
            // This feed arrives whole and already ordered, so the cap is ours to apply. Capping the
            // ordered list — rather than re-sorting the ranked slice — is what makes these the
            // soonest-due tasks instead of the top-ranked ones shuffled into date order.
            const items = dueDateFeed.items.filter(isFrameworkMemberTaskFeedItem);
            return {
                visibleItems: items.slice(0, PRIORITY_FEED_LIMIT),
                remainingCount: Math.max(0, items.length - PRIORITY_FEED_LIMIT),
            };
        }
        // Ranked: the server has already capped, and reports the pre-limit total.
        const items = (priorityQuery.data?.items ?? []).filter(isFrameworkMemberTaskFeedItem);
        return {
            visibleItems: items,
            remainingCount: Math.max(0, (priorityQuery.data?.totalItems ?? 0) - items.length),
        };
    }, [isDueDateOrder, dueDateFeed.items, priorityQuery.data]);

    const { panelScrollerRef, totalPanels, currentPanelIdx, canPrev, canNext, scrollToPanel } =
        useCarouselPanels(visibleItems);
    // Don't arm the celebration on a feed error: items collapses to 0 on failure, which would
    // otherwise fire a false "all done" celebration instead of just hiding the carousel.
    const celebration = useTaskCarouselCelebration(visibleItems.length, enableCelebration && !isError);
    // Hold off swapping in the celebration until the last card finishes animating out — keeping the
    // carousel (and its TransitionGroup) mounted lets that final exit animation play.
    const settledEmpty = useSettledEmpty(visibleItems.length === 0 || isError, location);

    if (settledEmpty) {
        if (celebration.show) {
            return <TaskCarouselCelebration onClose={celebration.dismiss} />;
        }
        return null;
    }

    return (
        <Stack
            data-mobile-flat
            spacing={2}
            sx={[
                {
                    bgcolor: 'cement.50',
                    pt: 2,
                    pb: 3,
                    px: { xs: 0, sm: 3 },
                    borderRadius: { xs: 0, sm: 4 },
                    '&&&': {
                        mx: { xs: -3, sm: 0 },
                    },
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}>
            {/* ---- Header (title + desktop pagination) ---- */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: { xs: 3, sm: 0 } }}>
                <Typography variant="h3" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>
                {totalPanels > 1 && (
                    <PanelNav
                        currentPanelIdx={currentPanelIdx}
                        totalPanels={totalPanels}
                        canPrev={canPrev}
                        canNext={canNext}
                        scrollToPanel={scrollToPanel}
                    />
                )}
            </Stack>

            {/* ---- Panel scroller ---- */}
            <Stack
                ref={panelScrollerRef}
                data-desktop-no-scroll
                direction="row"
                alignItems="stretch"
                gap={2}
                sx={{
                    overflowX: 'auto',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                    '& > *': { flexShrink: 0 },
                    px: { xs: 3, sm: 0 },
                    '@media (hover: hover)': { overflowX: 'hidden' },
                }}>
                {/* component={null} keeps each card a direct child of the scroller so panel
                    measurement (useCarouselPanels) still sees per-card bounding rects. Keyed by
                    surface so switching surfaces remounts instead of animating the swap. */}
                <TransitionGroup component={null} key={location ?? 'all'}>
                    {visibleItems.map((item) => (
                        <FeedItemTransition key={memberTaskFeedItemKey(item)} orientation="horizontal">
                            <CarouselItem item={item} page={location} />
                        </FeedItemTransition>
                    ))}
                </TransitionGroup>
                {remainingCount > 0 && (
                    <RemainingCard count={remainingCount} area={location ? LOCATION_TO_AREA[location] : undefined} />
                )}
            </Stack>
        </Stack>
    );
}
